import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiActivity, 
  FiCheckCircle, 
  FiXCircle, 
  FiClock, 
  FiTarget,
  FiUser,
  FiFileText,
  FiTrendingUp
} = FiIcons;

const OpportunityTracker = ({ productId }) => {
  // Mock data based on the provided image
  const opportunityData = [
    {
      id: '25-234',
      date: '2025-11-18',
      type: 'NBO (New Business Opportunity)',
      customer: 'ClarusOne',
      status: 'Won',
      accountHolder: 'Anne Sather'
    },
    {
      id: '25-235',
      date: '2025-11-19',
      type: 'ROFR (Right of First Refusal)',
      customer: 'RX SS',
      status: 'Loss',
      accountHolder: 'Charlie Murray'
    },
    {
      id: '25-236',
      date: '2025-11-20',
      type: 'OTB (One Time Buy)',
      customer: 'Optisource',
      status: 'Pending',
      accountHolder: 'Kyle Grupas'
    },
    {
      id: '25-237',
      date: '2025-11-21',
      type: 'SD (Short Dated)',
      customer: 'LeeSar',
      status: 'Loss',
      accountHolder: 'Kyle Grupas'
    },
    {
      id: '25-238',
      date: '2025-11-22',
      type: 'NBO (New Business Opportunity)',
      customer: 'Bonita',
      status: 'Pending',
      accountHolder: 'Anne Sather'
    },
    {
      id: '25-239',
      date: '2025-11-23',
      type: 'ROFR (Right of First Refusal)',
      customer: 'Cencora',
      status: 'Won',
      accountHolder: 'Charlie Murray'
    },
    {
      id: '25-240',
      date: '2025-11-24',
      type: 'OTB (One Time Buy)',
      customer: 'Walgreens',
      status: 'Won',
      accountHolder: 'Anne Sather'
    },
    {
      id: '25-241',
      date: '2025-11-25',
      type: 'SD (Short Dated)',
      customer: 'Publix',
      status: 'Won',
      accountHolder: 'Anne Sather'
    },
    {
      id: '25-242',
      date: '2025-11-26',
      type: 'NBO (New Business Opportunity)',
      customer: 'MMCAP',
      status: 'Pending',
      accountHolder: 'Charlie Murray'
    },
    {
      id: '25-243',
      date: '2025-11-27',
      type: 'ROFR (Right of First Refusal)',
      customer: 'Hercules',
      status: 'Loss',
      accountHolder: 'Anne Sather'
    },
    {
      id: '25-244',
      date: '2025-11-28',
      type: 'OTB (One Time Buy)',
      customer: 'RX SS',
      status: 'Loss',
      accountHolder: 'Charlie Murray'
    },
    {
      id: '25-245',
      date: '2025-11-29',
      type: 'SD (Short Dated)',
      customer: 'Optisource',
      status: 'Won',
      accountHolder: 'Charlie Murray'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Won':
        return { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle };
      case 'Loss':
        return { bg: 'bg-red-100', text: 'text-red-800', icon: FiXCircle };
      case 'Pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', icon: FiActivity };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate KPIs
  const totalOpportunities = opportunityData.length;
  const wins = opportunityData.filter(o => o.status === 'Won').length;
  const losses = opportunityData.filter(o => o.status === 'Loss').length;
  const pending = opportunityData.filter(o => o.status === 'Pending').length;
  const winRate = totalOpportunities > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* KPIs Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Total Opportunities</p>
              <h3 className="text-2xl font-bold text-gray-900">{totalOpportunities}</h3>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-blue-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiTrendingUp} className="text-blue-500 mr-1" />
            <span>Active pipeline tracking</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Win Rate</p>
              <h3 className="text-2xl font-bold text-gray-900">{winRate}%</h3>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCheckCircle} className="text-green-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <span>Based on closed opportunities</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Pending Bids</p>
              <h3 className="text-2xl font-bold text-gray-900">{pending}</h3>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClock} className="text-yellow-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <span>Awaiting decision</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Recent Wins</p>
              <h3 className="text-2xl font-bold text-gray-900">{wins}</h3>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiActivity} className="text-purple-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <span>Successful closures</span>
          </div>
        </div>
      </motion.div>

      {/* Opportunity Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiFileText} className="text-blue-600" />
            Bid Win/Loss Statement
          </h3>
          <p className="text-gray-600 mt-1">Detailed tracking of all bid opportunities and their outcomes</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Opportunity Type</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Customer Name</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">National Sales Account Holder</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">BID ID</th>
              </tr>
            </thead>
            <tbody>
              {opportunityData.map((row, index) => {
                const statusStyle = getStatusColor(row.status);
                return (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-gray-900 font-medium">
                      {formatDate(row.date)}
                    </td>
                    <td className="py-4 px-6 text-gray-700">
                      {row.type}
                    </td>
                    <td className="py-4 px-6 text-gray-900 font-medium">
                      {row.customer}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        <SafeIcon icon={statusStyle.icon} className="text-xs" />
                        {row.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiUser} className="text-gray-400" />
                        {row.accountHolder}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600 font-mono text-sm">
                      {row.id}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default OpportunityTracker;